// src/components/RestaurantLanding.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import RestaurantSetup from "./RestaurantSetup";
import "./RestaurantLanding.css";

const RestaurantLanding = () => {
  const navigate = useNavigate();
  const [showSetup, setShowSetup] = useState(false);

  const handleGetStarted = () => {
    setShowSetup(true);
  };

  const handleSetupComplete = (restaurant) => {
    // Navigate to the new restaurant's admin dashboard
    navigate(`/restaurant/${restaurant.slug}/admin`);
  };

  if (showSetup) {
    return <RestaurantSetup onComplete={handleSetupComplete} />;
  }

  return (
    <div className="restaurant-landing">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-branding">
              <div className="sommelai-logo">
                <img
                  src="/sommelai-logo.svg"
                  alt="SommelAI"
                  className="logo-image"
                />
                <div className="brand-text">
                  <h1 className="brand-name">SommelAI</h1>
                  <p className="brand-tagline">
                    AI-driven wine, beer, and spirits expertise for restaurants
                  </p>
                </div>
              </div>
            </div>

            <div className="features-list">
              <ul>
                <li>Professional ratings</li>
                <li>Tasting notes</li>
                <li>Historical context</li>
                <li>Food pairings</li>
                <li>Cocktail recipes</li>
              </ul>
            </div>

            <div className="hero-actions">
              <button onClick={handleGetStarted} className="cta-primary">
                Get Started Free
              </button>
              <button
                onClick={() => navigate("/demo/phase2-pairing")}
                className="cta-secondary"
              >
                View AI Pairing System
              </button>
            </div>
            <p className="trial-note">
              30-day free trial • No credit card required
            </p>
          </div>
          <div className="hero-image">
            <div className="restaurant-scene">
              <div className="wine-glasses">
                <div className="wine-glass red-wine"></div>
                <div className="wine-glass white-wine"></div>
              </div>
              <div className="table-setting">
                <div className="plate"></div>
                <div className="utensils"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2>Everything You Need for Digital Menu Success</h2>
          <div className="features-grid">
            <div className="feature">
              <div className="feature-icon">📄</div>
              <h3>Automatic Menu Import</h3>
              <p>
                Upload your PDF or Word menus and our AI will automatically
                extract and organize your items.
              </p>
            </div>
            <div className="feature">
              <div className="feature-icon">🎨</div>
              <h3>Custom Branding</h3>
              <p>
                Match your restaurant's style with custom colors, fonts, and
                themes.
              </p>
            </div>
            <div className="feature">
              <div className="feature-icon">📱</div>
              <h3>Mobile Optimized</h3>
              <p>
                Your menus look perfect on any device - phones, tablets, and
                desktops.
              </p>
            </div>
            <div className="feature">
              <div className="feature-icon">🔄</div>
              <h3>Real-time Updates</h3>
              <p>
                Update prices and availability instantly. Changes appear
                immediately on all devices.
              </p>
            </div>
            <div className="feature">
              <div className="feature-icon">🍷</div>
              <h3>Wine & Spirit Tools</h3>
              <p>
                Special features for wine lists including comparison tools and
                detailed descriptions.
              </p>
            </div>
            <div className="feature">
              <div className="feature-icon">📊</div>
              <h3>Analytics Dashboard</h3>
              <p>Track popular items, peak hours, and customer preferences.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <h2>Get Started in Minutes</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Upload Your Menu</h3>
                <p>
                  Simply upload your existing menu documents (PDF, Word, or
                  images)
                </p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Customize Your Brand</h3>
                <p>
                  Choose colors, fonts, and styling that match your restaurant
                </p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Go Live</h3>
                <p>
                  Share your menu link with customers or embed it on your
                  website
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing">
        <div className="container">
          <h2>Simple, Transparent Pricing</h2>
          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Free</h3>
                <div className="price">
                  $0<span>/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>Up to 100 menu items</li>
                <li>Basic customization</li>
                <li>Mobile-friendly menus</li>
                <li>Document upload</li>
                <li>Email support</li>
              </ul>
              <button onClick={handleGetStarted} className="pricing-button">
                Start Free
              </button>
            </div>

            <div className="pricing-card featured">
              <div className="pricing-header">
                <h3>Professional</h3>
                <div className="price">
                  $29<span>/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>Unlimited menu items</li>
                <li>Advanced customization</li>
                <li>Custom domain</li>
                <li>Analytics dashboard</li>
                <li>Priority support</li>
                <li>Wine comparison tools</li>
              </ul>
              <button onClick={handleGetStarted} className="pricing-button">
                Start Free Trial
              </button>
            </div>

            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Enterprise</h3>
                <div className="price">Custom</div>
              </div>
              <ul className="pricing-features">
                <li>Multiple locations</li>
                <li>Advanced analytics</li>
                <li>API access</li>
                <li>White-label options</li>
                <li>Dedicated support</li>
                <li>Custom integrations</li>
              </ul>
              <button className="pricing-button">Contact Sales</button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta">
        <div className="container">
          <h2>Ready to Modernize Your Menu?</h2>
          <p>Join hundreds of restaurants already using our platform</p>
          <button onClick={handleGetStarted} className="cta-primary large">
            Get Started Free Today
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Product</h4>
              <ul>
                <li>
                  <a href="#features">Features</a>
                </li>
                <li>
                  <a href="#pricing">Pricing</a>
                </li>
                <li>
                  <a href="/demo">Demo</a>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Support</h4>
              <ul>
                <li>
                  <a href="/help">Help Center</a>
                </li>
                <li>
                  <a href="/contact">Contact</a>
                </li>
                <li>
                  <a href="/docs">Documentation</a>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li>
                  <a href="/about">About</a>
                </li>
                <li>
                  <a href="/privacy">Privacy</a>
                </li>
                <li>
                  <a href="/terms">Terms</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 SommelAI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RestaurantLanding;
